import React from "react";
import CollapsibleSection from "./CollapsibleSection";
import {
    formatFflExpirationDate,
    formatPhoneNumber,
    getFflExpirationDaysLeft,
    getFflExpirationClass
} from '../utils/functions'

const DealershipInfoPanel = ({
    isOpen,
    onToggle,
    fflNumber,
    setFflNumber,
    fflExpiration,
    fflName,
    setFflName,
    businessName,
    setBusinessName,
    phoneNumber,
    optionalPhoneNumber,
    setOptionalPhoneNumber,
    streetAddress,
    cityStateZip,
    email,
    setEmail,
    website,
    setWebsite,
    updateLocalStorage,
    handleFFLChange,
    fflInputRef,
    fflNameRef,
    fflBusinessNameRef,
}) => {
    const validExpiration = Boolean(fflExpiration) && Number.isFinite(new Date(fflExpiration).getTime());
    const readOnly = localStorage.getItem("fflClaimedOnce") === "true";

    return (
        <CollapsibleSection
            title="🏪 Dealership Info"
            isOpen={isOpen}
            onToggle={onToggle}
        >
            <div className="form-section">
                <br />
                <label>FFL Number</label>
                <span className="readonly-note">(auto-filled from ATF)</span>
                <div className="input-container">
                    <input
                        ref={fflInputRef}
                        type="text"
                        value={fflNumber}
                        onChange={handleFFLChange}
                        placeholder="0-00-000-00-0X-00000"
                        readOnly={readOnly}
                    />
                </div>

                {(
                    <div className="ffl-expiration-note">
                        <small className="notes">
                            Expiration Date:{" "}
                            <span className={`claim-date ${validExpiration ? getFflExpirationClass(fflExpiration) : ""}`}>
                                {validExpiration ? `${formatFflExpirationDate(fflExpiration)} (${getFflExpirationDaysLeft(fflExpiration)} days left)` : "Not available"}
                            </span>
                        </small>
                    </div>
                )}

                <div className="space-div" />
                <label>FFL License Name</label>
                <span className="readonly-note">(auto-filled from ATF)</span>
                <input
                    ref={fflNameRef}
                    type="text"
                    value={fflName}
                    onChange={(e) => {
                        const value = e.target.value;
                        setFflName(value);
                        updateLocalStorage({ fflName: value });
                    }}
                    placeholder="Enter FFL Name"
                    readOnly={readOnly}
                />

                <div className="space-div" />
                <label>FFL Business Name</label>
                <span className="readonly-note">(auto-filled from ATF)</span>
                <input
                    ref={fflBusinessNameRef}
                    type="text"
                    value={businessName}
                    onChange={(e) => {
                        const value = e.target.value;
                        setBusinessName(value);
                        updateLocalStorage({ businessName: value });
                    }}
                    placeholder="Enter FFL Name"
                    readOnly={readOnly}
                />

                <div className="space-div" />
                <div className="readonly-info-block">
                    <label>Business Phone</label>
                    <span className="readonly-note">(auto-filled from ATF)</span>
                    <input
                        type="text"
                        value={phoneNumber || "Not available"}
                        readOnly
                    />

                    <div className="space-div" />
                    <label>Optional Dealer Contact Phone</label>
                    <input
                        type="tel"
                        className="dealer-editable-contact"
                        value={optionalPhoneNumber || ""}
                        onChange={(e) => {
                            const input = e.target;
                            const value = input.value;
                            const deleting = e.nativeEvent?.inputType?.startsWith('delete');
                            const digits = value.replace(/\D/g, '');
                            const formatted = !deleting && /^[\d\s().-]+$/.test(value) && digits.length === 10
                                ? formatPhoneNumber(value) : value;
                            const digitsBeforeCaret = value.slice(0, input.selectionStart ?? value.length).replace(/\D/g, '').length;
                            setOptionalPhoneNumber(formatted);
                            if (formatted !== value) {
                                requestAnimationFrame(() => {
                                    let position = 0, count = 0;
                                    while (position < formatted.length && count < digitsBeforeCaret) {
                                        if (/\d/.test(formatted[position])) count++;
                                        position++;
                                    }
                                    input.setSelectionRange(position, position);
                                });
                            }
                        }}
                        placeholder="(optional) e.g. (555) 987-6543"
                    />
                    <small className="note">
                        This number will also appear on your public profile if filled.
                    </small>

                    <label>Business Address</label>
                    <span className="readonly-note">(auto-filled from ATF)</span>
                    <input type="text" value={streetAddress || "Street not available"} readOnly />
                    <input type="text" value={cityStateZip || ""} readOnly />
                </div>

                <div className="space-div" />
                <label>Email</label>
                <span className="readonly-note">(read-only)</span>
                <input
                    type="email"
                    value={email}
                    onChange={(e) => {
                        const value = e.target.value;
                        setEmail(value);
                        updateLocalStorage({ email: value });
                    }}
                    readOnly={readOnly}
                    placeholder="Enter your contact email"
                />

                <div className="space-div" />
                <label>Website</label>
                <div className="space-div" />
                <small className="note">
                    External Website (Optional): If you already have your own website,
                    enter it here so we can provide a link to it.
                </small>
                <input
                    type="url"
                        className="dealer-editable-contact"
                    value={website}
                    onChange={(e) => {
                        const value = e.target.value;
                        setWebsite(value);
                        updateLocalStorage({ website: value });
                    }}
                    placeholder="Enter your website URL"
                />
            </div>
        </CollapsibleSection>
    );
};

export default DealershipInfoPanel;